import React, { useState, useMemo, useRef, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Smile, Hash, Info, FileText, BookOpen, MessagesSquare, Layers, Eye, ArrowLeft, Settings, Lightbulb, Download, Copy, Check } from 'lucide-react';
import html2canvas from 'html2canvas';
import { useWizard } from '../../../context/WizardContext';
import ContentAnalysis from '../Analytics/ContentAnalysis';
import NarrativeAnalysis from '../Analytics/NarrativeAnalysis';
import DiscourseAnalysis from '../Analytics/DiscourseAnalysis';
import GroundedTheory from '../Analytics/GroundedTheory';
import PhenomenologicalAnalysis from '../Analytics/PhenomenologicalAnalysis';
import { positiveWords, negativeWords, subjectiveWords, stopWords, tokenizeAll } from '../Analytics/qualitativeUtils';
import { splitIntoSegments } from './documentParser';
import { saveAnalysisSession } from './SavedAnalysesList';
import '../Analytics/TextAnalysis.css';
import './Documents.css';

const COLORS = ['#22c55e', '#f59e0b', '#ef4444'];
const POLARITY_COLORS = ['#22c55e', '#94a3b8', '#ef4444'];
const SUBJECTIVITY_COLORS = ['#6366f1', '#a5b4fc'];

const analysisMethods = [
  { id: 'sentiment', name: 'Sentiment', icon: Smile, methodKey: 'sentimentAnalysis' },
  { id: 'thematic', name: 'Thematic', icon: Hash, methodKey: 'thematicAnalysis' },
  { id: 'content', name: 'Content', icon: FileText, methodKey: 'contentAnalysis' },
  { id: 'narrative', name: 'Narrative', icon: BookOpen, methodKey: 'narrativeAnalysis' },
  { id: 'discourse', name: 'Discourse', icon: MessagesSquare, methodKey: 'discourseAnalysis' },
  { id: 'grounded', name: 'Grounded Theory', icon: Layers, methodKey: 'groundedTheory' },
  { id: 'phenomenological', name: 'Phenomenological', icon: Eye, methodKey: 'phenomenologicalAnalysis' }
];

const DocumentAnalysis = ({ document, initialMethod, onBack }) => {
  const { dataB } = useWizard();
  const [activeMethod, setActiveMethod] = useState(initialMethod || 'sentiment');
  const [segmentType, setSegmentType] = useState('paragraphs');
  const [copiedSummary, setCopiedSummary] = useState(null);
  const thematicChartRef = useRef(null);
  const sentimentChartsRef = useRef(null);

  useEffect(() => {
    saveAnalysisSession(document.id, activeMethod);
  }, [document.id, activeMethod]);

  const downloadChartAsPng = async (chartRef, filename) => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const link = window.document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading chart:', error);
    }
  };

  const copyToClipboard = async (text, summaryId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSummary(summaryId);
      setTimeout(() => setCopiedSummary(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const plannedMethods = useMemo(() => {
    const methods = dataB?.qualitativeMethods || {};
    return Object.entries(methods)
      .filter(([_, enabled]) => enabled)
      .map(([key]) => key);
  }, [dataB]);

  const segments = useMemo(() => {
    return splitIntoSegments(document.text, segmentType);
  }, [document.text, segmentType]);

  const analyzeSentiment = (text) => {
    const words = tokenizeAll(text);
    let positive = 0, negative = 0, subjective = 0;
    
    words.forEach(word => {
      if (positiveWords.has(word)) positive++;
      if (negativeWords.has(word)) negative++;
      if (subjectiveWords.has(word)) subjective++;
    });
    
    const total = positive + negative;
    const subjectivityScore = words.length > 0 ? subjective / words.length : 0;
    if (total === 0) return { sentiment: 'neutral', score: 0, positive, negative, subjective, subjectivityScore, objective: words.length - subjective };
    
    const score = (positive - negative) / total;
    let sentiment = 'neutral';
    if (score > 0.2) sentiment = 'positive';
    else if (score < -0.2) sentiment = 'negative';
    
    return { sentiment, score, positive, negative, subjective, subjectivityScore, objective: words.length - subjective };
  };

  const sentimentData = useMemo(() => {
    const results = segments.map(seg => analyzeSentiment(seg));
    const counts = { positive: 0, neutral: 0, negative: 0 };
    results.forEach(r => counts[r.sentiment]++);
    
    const totalPositive = results.reduce((sum, r) => sum + r.positive, 0);
    const totalNegative = results.reduce((sum, r) => sum + r.negative, 0);
    const totalSubjective = results.reduce((sum, r) => sum + r.subjective, 0);
    const totalObjective = results.reduce((sum, r) => sum + r.objective, 0);
    const totalSentimentWords = totalPositive + totalNegative;
    const totalWords = totalSubjective + totalObjective;
    
    const positivePercent = totalSentimentWords > 0 ? Math.round((totalPositive / totalSentimentWords) * 100) : 0;
    const negativePercent = totalSentimentWords > 0 ? Math.round((totalNegative / totalSentimentWords) * 100) : 0;
    const neutralPercent = 100 - positivePercent - negativePercent;
    
    const subjectivePercent = totalWords > 0 ? Math.round((totalSubjective / totalWords) * 100) : 0;
    const objectivePercent = 100 - subjectivePercent;
    
    const avgScore = results.length > 0 
      ? (results.reduce((sum, r) => sum + r.score, 0) / results.length)
      : 0;
    
    return {
      distribution: [
        { name: 'Positive', value: counts.positive },
        { name: 'Neutral', value: counts.neutral },
        { name: 'Negative', value: counts.negative }
      ],
      polarityData: [
        { name: 'Positive', value: totalPositive, percent: positivePercent },
        { name: 'Neutral', value: Math.max(0, segments.length - counts.positive - counts.negative), percent: neutralPercent },
        { name: 'Negative', value: totalNegative, percent: negativePercent }
      ],
      subjectivityData: [
        { name: 'Subjective', value: totalSubjective, percent: subjectivePercent },
        { name: 'Objective', value: totalObjective, percent: objectivePercent }
      ],
      totalPositive,
      totalNegative,
      totalSubjective,
      totalObjective,
      positivePercent,
      negativePercent,
      subjectivePercent,
      objectivePercent,
      avgScore: avgScore.toFixed(2),
      segmentCount: segments.length,
      positiveSegments: counts.positive,
      neutralSegments: counts.neutral,
      negativeSegments: counts.negative
    };
  }, [segments]);

  const thematicData = useMemo(() => {
    const allText = document.text.toLowerCase();
    const words = tokenizeAll(allText);
    const filtered = words.filter(w => !stopWords.has(w) && w.length > 3);
    
    const freq = {};
    filtered.forEach(word => {
      freq[word] = (freq[word] || 0) + 1;
    });
    
    const themes = Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([word, count]) => ({ word, count }));
    
    return themes;
  }, [document.text]);

  const renderMethodContent = () => {
    switch (activeMethod) {
      case 'sentiment':
        const sentimentSummary = `This sentiment analysis examined ${sentimentData.segmentCount} text segments (segmented by ${segmentType}) from the uploaded document, identifying emotional tone through lexical analysis. The polarity distribution reveals ${sentimentData.positivePercent}% positive sentiment (${sentimentData.totalPositive} positive words detected) versus ${sentimentData.negativePercent}% negative sentiment (${sentimentData.totalNegative} negative words detected), yielding an average sentiment score of ${sentimentData.avgScore} on a scale from -1 (entirely negative) to +1 (entirely positive). Of all segments analyzed, ${sentimentData.positiveSegments} were classified as predominantly positive, ${sentimentData.negativeSegments} as negative, and ${sentimentData.neutralSegments} as neutral. The subjectivity analysis indicates ${sentimentData.subjectivePercent}% of the content contains subjective language (opinion-based expressions, personal evaluations, and emotional descriptors) while ${sentimentData.objectivePercent}% maintains objective, factual language. This subjective-objective ratio suggests the text ${sentimentData.subjectivePercent > 50 ? 'leans toward personal opinions and emotional expression' : 'maintains a relatively factual and objective tone'}. Researchers should consider how these sentiment patterns align with or contradict findings from other data sources, and examine whether specific topics or themes correlate with positive or negative emotional expression within the dataset.`;
        
        return (
          <div className="analysis-section sentiment-analysis">
            <div className="analysis-header">
              <h4><Smile size={20} /> Sentiment Analysis</h4>
              <p>Analyze the overall emotional tone across the document</p>
            </div>

            <div className="analysis-guidance">
              <Lightbulb size={18} />
              <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
            </div>
            
            <div className="stats-row">
              <div className="stat-box">
                <div className="stat-value">{segments.length}</div>
                <div className="stat-label">Segments Analyzed</div>
              </div>
              <div className="stat-box positive">
                <div className="stat-value">{sentimentData.totalPositive}</div>
                <div className="stat-label">Positive Words</div>
              </div>
              <div className="stat-box negative">
                <div className="stat-value">{sentimentData.totalNegative}</div>
                <div className="stat-label">Negative Words</div>
              </div>
              <div className="stat-box highlight">
                <div className="stat-value">{sentimentData.avgScore}</div>
                <div className="stat-label">Avg Sentiment Score</div>
              </div>
            </div>
            
            <div className="chart-section">
              <h5>Sentiment Distribution by Segment</h5>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                  <Pie
                    data={sentimentData.distribution}
                    cx="50%"
                    cy="45%"
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, value, name }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return value > 0 ? (
                        <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
                          {`${name}: ${value}`}
                        </text>
                      ) : null;
                    }}
                  >
                    {sentimentData.distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ paddingTop: 15 }} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="two-column-charts" ref={sentimentChartsRef}>
              <div className="chart-section">
                <h5>Polarity Distribution (%)</h5>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={sentimentData.polarityData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={45} />
                    <Tooltip formatter={(value, name, props) => [`${props.payload.percent}%`, name]} />
                    <Bar dataKey="percent" radius={[4, 4, 0, 0]}>
                      {sentimentData.polarityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={POLARITY_COLORS[index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="chart-legend">
                  <span className="legend-item"><span className="dot" style={{background: '#22c55e'}}></span>Positive: {sentimentData.positivePercent}%</span>
                  <span className="legend-item"><span className="dot" style={{background: '#94a3b8'}}></span>Neutral</span>
                  <span className="legend-item"><span className="dot" style={{background: '#ef4444'}}></span>Negative: {sentimentData.negativePercent}%</span>
                </div>
              </div>

              <div className="chart-section">
                <h5>Subjectivity Distribution (%)</h5>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <Pie
                      data={sentimentData.subjectivityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      dataKey="value"
                      labelLine={false}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        if (percent === 0) return null;
                        const RADIAN = Math.PI / 180;
                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="600">
                            {`${(percent * 100).toFixed(0)}%`}
                          </text>
                        );
                      }}
                    >
                      {sentimentData.subjectivityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={SUBJECTIVITY_COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [`${props.payload.percent}%`, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="chart-legend centered">
                  <span className="legend-item"><span className="dot" style={{background: '#6366f1'}}></span>Subjective: {sentimentData.subjectivePercent}%</span>
                  <span className="legend-item"><span className="dot" style={{background: '#a5b4fc'}}></span>Objective: {sentimentData.objectivePercent}%</span>
                </div>
              </div>
            </div>

            <div className="chart-actions">
              <button 
                className="action-btn download-btn"
                onClick={() => downloadChartAsPng(sentimentChartsRef, `sentiment-analysis-${document.title}`)}
              >
                <Download size={16} /> Download Charts (PNG)
              </button>
            </div>

            <div className="interpretation-section">
              <div className="summary-header">
                <h5><Info size={18} /> Analytical Summary</h5>
                <button 
                  className={`action-btn copy-btn ${copiedSummary === 'sentiment' ? 'copied' : ''}`}
                  onClick={() => copyToClipboard(sentimentSummary, 'sentiment')}
                >
                  {copiedSummary === 'sentiment' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy</>}
                </button>
              </div>
              <p>{sentimentSummary}</p>
            </div>
          </div>
        );
        
      case 'thematic':
        const topTheme = thematicData[0];
        const secondTheme = thematicData[1];
        const thirdTheme = thematicData[2];
        const totalThemeOccurrences = thematicData.reduce((sum, t) => sum + t.count, 0);
        const topThreePercent = topTheme && secondTheme && thirdTheme 
          ? Math.round(((topTheme.count + secondTheme.count + thirdTheme.count) / totalThemeOccurrences) * 100) 
          : 0;
        
        const thematicSummary = `This thematic analysis processed ${document.wordCount.toLocaleString()} words from the document, segmented by ${segmentType} (${segments.length} segments), identifying ${thematicData.length} key recurring themes through frequency-based extraction. The most prominent theme is "${topTheme?.word || 'N/A'}" appearing ${topTheme?.count || 0} times, followed by "${secondTheme?.word || 'N/A'}" (${secondTheme?.count || 0} occurrences) and "${thirdTheme?.word || 'N/A'}" (${thirdTheme?.count || 0} occurrences). These top three themes account for approximately ${topThreePercent}% of all thematic content identified. The ${segmentType}-based segmentation approach ${segmentType === 'sentences' ? 'provides granular analysis of individual statements' : segmentType === 'paragraphs' ? 'captures broader contextual meaning within each text block' : 'follows the natural line structure of the document'}. The frequency distribution suggests ${topTheme?.count > 20 ? 'strong thematic concentration around specific concepts' : 'a relatively distributed spread of themes across the text'}. Researchers should examine whether these prominent themes align with the research questions and theoretical framework, and consider how they may relate to themes identified in other data sources. The co-occurrence of certain themes may indicate conceptual relationships worth exploring through axial coding or framework mapping.`;
        
        return (
          <div className="analysis-section thematic-analysis">
            <div className="analysis-header">
              <h4><Hash size={20} /> Thematic Analysis</h4>
              <p>Identify recurring themes and patterns in the document</p>
            </div>

            <div className="analysis-guidance">
              <Lightbulb size={18} />
              <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
            </div>
            
            <div className="stats-row">
              <div className="stat-box">
                <div className="stat-value">{document.wordCount.toLocaleString()}</div>
                <div className="stat-label">Total Words</div>
              </div>
              <div className="stat-box highlight">
                <div className="stat-value">{thematicData.length}</div>
                <div className="stat-label">Key Themes Found</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{totalThemeOccurrences}</div>
                <div className="stat-label">Total Theme Occurrences</div>
              </div>
            </div>
            
            <div className="chart-section" ref={thematicChartRef}>
              <h5>Top Themes by Frequency</h5>
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={thematicData} layout="vertical" margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="word" type="category" width={120} tick={{ fontSize: 11 }} interval={0} />
                  <Tooltip formatter={(value) => [`${value} occurrences`, 'Frequency']} />
                  <Bar dataKey="count" fill="#9333ea" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-actions">
              <button 
                className="action-btn download-btn"
                onClick={() => downloadChartAsPng(thematicChartRef, `thematic-analysis-${document.title}`)}
              >
                <Download size={16} /> Download Chart (PNG)
              </button>
            </div>

            <div className="interpretation-section">
              <div className="summary-header">
                <h5><Info size={18} /> Analytical Summary</h5>
                <button 
                  className={`action-btn copy-btn ${copiedSummary === 'thematic' ? 'copied' : ''}`}
                  onClick={() => copyToClipboard(thematicSummary, 'thematic')}
                >
                  {copiedSummary === 'thematic' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy</>}
                </button>
              </div>
              <p>{thematicSummary}</p>
            </div>
          </div>
        );
        
      case 'content':
        return <ContentAnalysis texts={segments} surveyId={`doc-${document.id}`} />;
        
      case 'narrative':
        return <NarrativeAnalysis texts={segments} />;
        
      case 'discourse':
        return <DiscourseAnalysis texts={segments} />;
        
      case 'grounded':
        return <GroundedTheory texts={segments} surveyId={`doc-${document.id}`} />;
        
      case 'phenomenological':
        return <PhenomenologicalAnalysis texts={segments} surveyId={`doc-${document.id}`} />;
        
      default:
        return null;
    }
  };

  return (
    <div className="document-analysis-container">
      <div className="analysis-nav">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={18} />
          Back to Library
        </button>
        <div className="doc-info-bar">
          <FileText size={18} />
          <span className="doc-name">{document.title}</span>
          <span className="doc-words">{document.wordCount.toLocaleString()} words</span>
        </div>
      </div>

      <div className="segmentation-controls">
        <span><Settings size={16} /> Segment by:</span>
        <select value={segmentType} onChange={(e) => setSegmentType(e.target.value)}>
          <option value="paragraphs">Paragraphs</option>
          <option value="sentences">Sentences</option>
          <option value="lines">Lines</option>
        </select>
        <span className="segment-count">{segments.length} segments</span>
      </div>

      <div className="text-analysis-container">
        <div className="method-tabs">
          {analysisMethods.map(method => {
            const Icon = method.icon;
            const isPlanned = plannedMethods.includes(method.methodKey);
            return (
              <button
                key={method.id}
                className={`method-tab ${activeMethod === method.id ? 'active' : ''} ${isPlanned ? 'planned' : ''}`}
                data-method={method.id}
                onClick={() => setActiveMethod(method.id)}
              >
                <Icon size={18} />
                <span>{method.name}</span>
                {isPlanned && <span className="planned-badge">Planned</span>}
              </button>
            );
          })}
        </div>

        <div className="analysis-content">
          {renderMethodContent()}
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysis;
